import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, RefreshCw, Smartphone } from "lucide-react";
import { ApiError, getDevice, postAction, startBridge } from "../lib/api";
import type { DeviceInfo, UIElement } from "../types";
import { DeviceFrame } from "../components/DeviceFrame";
import { FpsSelect } from "../components/FpsSelect";
import { DeviceControls } from "../components/DeviceControls";
import { getDefaultFps, setDefaultFps, useServerBase } from "../lib/settings";
import type { DragPayload, TapPayload } from "../lib/gesture";
import { useActionsWS } from "../lib/useActionsWS";

export function DeviceControl() {
  const { deviceId = "" } = useParams();
  const [serverBase] = useServerBase();
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [elements, setElements] = useState<UIElement[]>([]);
  const [fps, setFps] = useState<number>(() => getDefaultFps());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [status, setStatus] = useState<string>("");
  const [error, setError] = useState<string>("");
  // Bridge handshake state. We do an explicit POST /bridge/start before
  // mounting the action WS / MJPEG stream — both retry silently on failure,
  // so hitting the free-tier device limit would otherwise pound the server
  // forever. `limitReached` short-circuits that into an upgrade banner.
  const [bridgeReady, setBridgeReady] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const lastRefreshAt = useRef(0);

  // ---- Bridge handshake ---------------------------------------------------
  // Start the bridge explicitly once, so we can distinguish a transient
  // failure from a hard 402 tier limit. The action WS and MJPEG <img> both
  // retry silently on close/error, so without this gate a free-tier second
  // device would spin a reconnect loop forever.
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    setBridgeReady(false);
    setLimitReached(false);
    setError("");
    startBridge(deviceId)
      .then(() => {
        if (!cancelled) setBridgeReady(true);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === "device_limit_reached") {
          setLimitReached(true);
          return;
        }
        setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [serverBase, deviceId]);

  // ---- Device metadata: poll until we have screen dims. -------------------
  // The server only populates screenWidth/Height when the bridge is
  // connected, so on a cold start we need to keep refetching until it is.
  // Coordinate math in gestures depends on these, and the fallback values
  // would place taps wildly off-screen otherwise.
  useEffect(() => {
    if (!deviceId || !bridgeReady) return;
    let cancelled = false;
    let timer: number | null = null;
    const haveDims = (d: DeviceInfo | null): boolean =>
      !!(
        d &&
        d.screenWidth &&
        d.screenHeight &&
        d.screenWidth > 0 &&
        d.screenHeight > 0
      );

    const poll = async () => {
      try {
        const d = await getDevice(deviceId);
        if (cancelled) return;
        setDevice(d);
        setError("");
        if (!haveDims(d)) {
          timer = window.setTimeout(poll, 2000);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : String(e));
        timer = window.setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [serverBase, deviceId, bridgeReady]);

  // ---- UI tree: pushed over the actions WebSocket. ------------------------
  // Server sends the tree on connect and debounced after each action. No
  // HTTP polling — the WS path is quota-exempt and the push cadence
  // matches what we used to schedule locally.
  const actions = useActionsWS(bridgeReady && deviceId ? deviceId : null);
  useEffect(() => {
    if (actions.tree) {
      setElements(actions.tree.elements ?? []);
      lastRefreshAt.current = performance.now();
      setStatus("");
    }
  }, [actions.tree]);

  const refreshTree = useCallback(() => {
    actions.send({ type: "refresh" });
  }, [actions]);

  // ---- Action handlers -----------------------------------------------------
  // Fire-and-forget: bridges replay gestures synchronously (iOS WDA commits
  // the whole XCUIEventSynthesizer sequence as a batch; Android `input swipe`
  // blocks for `duration` ms). Awaiting the POST froze the UI for the full
  // playback time. Dispatch, unlock immediately, report failures async.
  const onTap = useCallback(
    (p: TapPayload) => {
      actions.send({ type: "tap", x: p.x, y: p.y });
    },
    [actions],
  );

  const onDrag = useCallback(
    (p: DragPayload) => {
      actions.send({
        type: "drag",
        fromX: p.fromX,
        fromY: p.fromY,
        toX: p.toX,
        toY: p.toY,
        duration: p.duration,
        pressDuration: p.pressDuration,
        holdDuration: p.holdDuration,
      });
    },
    [actions],
  );

  // Type still goes over HTTP — quota-paid, since text input is often
  // the "real" automation lever and we don't want to subsidize it.
  const onType = useCallback(
    (text: string) => {
      postAction(deviceId, "type", { text }).catch((e) => {
        setStatus(
          e instanceof ApiError ? `type: ${e.message}` : `type: ${String(e)}`,
        );
      });
    },
    [deviceId],
  );

  const onFps = useCallback((v: number) => {
    setFps(v);
    setDefaultFps(v);
  }, []);

  // ---- Hash-based screen-change detection for auto-refresh. ----------------
  useEffect(() => {
    if (!autoRefresh) return;
    const canvas = document.createElement("canvas");
    canvas.width = 16;
    canvas.height = 32;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    let last: Uint8ClampedArray | null = null;
    const id = window.setInterval(() => {
      const img = document.querySelector<HTMLImageElement>("#frame img");
      if (!img || !img.complete || !img.naturalWidth) return;
      try {
        ctx.drawImage(img, 0, 0, 16, 32);
        const next = ctx.getImageData(0, 0, 16, 32).data;
        if (last) {
          let diff = 0;
          for (let i = 0; i < next.length; i += 4) {
            diff +=
              Math.abs(next[i] - last[i]) +
              Math.abs(next[i + 1] - last[i + 1]) +
              Math.abs(next[i + 2] - last[i + 2]);
          }
          if (diff > 600 && performance.now() - lastRefreshAt.current > 1500) {
            lastRefreshAt.current = performance.now();
            refreshTree();
          }
        }
        last = next;
      } catch {
        /* CORS-tainted canvas — shouldn't happen for same-origin-relaxed localhost */
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [autoRefresh, refreshTree]);

  // The iOS bridge caches screen dims from the first WDA call, so once the
  // device rotates `device.screenWidth/Height` go stale. Track the live
  // orientation from the MJPEG stream and swap the axes when it doesn't
  // match the cached ones, so gesture handlers always see the current
  // orientation's dims.
  const [streamLandscape, setStreamLandscape] = useState<boolean | null>(null);

  // Stable identity — passed to DeviceFrame whose H.264 effect depends
  // on it. An inline closure would be a fresh reference every render
  // and would tear down + reopen the video WebSocket on every UI-tree
  // poll (visible as a black flash each second).
  const onOrientationChange = useCallback(
    (landscape: boolean) => {
      setStreamLandscape(landscape);
      refreshTree();
    },
    [refreshTree],
  );
  const cachedW = device?.screenWidth ?? 0;
  const cachedH = device?.screenHeight ?? 0;
  const cachedLandscape = cachedW > cachedH;
  const flipAxes =
    streamLandscape !== null && streamLandscape !== cachedLandscape;
  const w = flipAxes ? cachedH : cachedW;
  const h = flipAxes ? cachedW : cachedH;
  const dimsReady = w > 0 && h > 0;

  // ---- Hardware-button handlers -------------------------------------------
  const onHome = useCallback(() => {
    actions.send({ type: "press-key", key: "home" });
  }, [actions]);

  const onBack = useCallback(() => {
    actions.send({ type: "press-key", key: "back" });
  }, [actions]);

  const onAppSwitcher = useCallback(() => {
    if (!device) return;
    if (device.platform === "android") {
      actions.send({ type: "press-key", key: "recent_apps" });
    } else {
      // iOS: short swipe-up from the visual bottom edge. WDA rotates
      // to native coords server-side, so visual coords are fine for
      // both portrait and landscape.
      const fromY = h - 2;
      actions.send({
        type: "drag",
        fromX: Math.round(w / 2),
        fromY,
        toX: Math.round(w / 2),
        toY: fromY - Math.round(h * 0.26),
        duration: 454,
        pressDuration: 233,
        holdDuration: 0,
      });
    }
  }, [device, w, h, actions]);

  return (
    <div className="mx-auto max-w-4xl h-full px-4 py-3 flex flex-col gap-2">
      {/* Viewer settings: local to the page — stream cadence + overlay
          refresh behaviour. Visually separated so it doesn't read as
          something that affects the device. */}
      <div className="w-full flex flex-wrap items-center justify-end gap-3 text-xs text-surface-500 border-t border-surface-800/70 pt-2">
        <span className="uppercase tracking-wide text-[10px] text-surface-600">
          Viewer
        </span>
        <FpsSelect value={fps} onChange={onFps} />
        <div className="inline-flex rounded-md border border-surface-700 overflow-hidden">
          <button
            type="button"
            onClick={() => setAutoRefresh((v) => !v)}
            className={`px-2 py-1 text-xs transition-colors ${
              autoRefresh
                ? "bg-accent-cyan/15 text-accent-cyan"
                : "text-surface-400 hover:bg-surface-800"
            }`}
            title="Auto-refresh UI tree when the screen changes"
          >
            Auto-refresh
          </button>
          <button
            type="button"
            onClick={refreshTree}
            className="px-2 py-1 border-l border-surface-700 text-surface-300 hover:bg-surface-800 inline-flex items-center gap-1"
            title="Re-fetch UI tree now"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="w-full p-3 rounded-md border border-rose-500/30 bg-rose-500/10 text-sm text-rose-300">
          {error}
        </div>
      )}

      {limitReached ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 text-center max-w-md mx-auto">
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full bg-amber-400/20 blur-xl"
            />
            <div className="relative w-16 h-16 rounded-2xl bg-surface-800 border border-amber-400/40 flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-amber-300" />
            </div>
          </div>
          <div>
            <div className="text-sm text-surface-100 font-medium">
              Device limit reached
            </div>
            <div className="mt-1 text-xs text-surface-400">
              Your plan supports one active device at a time. Stop the other
              bridge in MobAI — or upgrade to Pro for unlimited devices — and
              reload this page.
            </div>
          </div>
        </div>
      ) : dimsReady ? (
        <>
          {/* Frame takes whatever vertical space is left after toolbar/
              controls panel. min-h-0 is required for flex children to
              actually shrink inside a flex parent. */}
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <DeviceFrame
              deviceId={deviceId}
              deviceWidth={w}
              deviceHeight={h}
              fps={fps}
              elements={elements}
              onTap={onTap}
              onDrag={onDrag}
              onOrientationChange={onOrientationChange}
            />
          </div>
          <DeviceControls
            platform={device?.platform}
            deviceWidth={w}
            deviceHeight={h}
            disabled={!dimsReady}
            onHome={onHome}
            onBack={onBack}
            onAppSwitcher={onAppSwitcher}
            onType={onType}
          />
          {status && (
            <span className="text-xs text-rose-400 shrink-0">{status}</span>
          )}
        </>
      ) : (
        // Don't render the frame until we have real device dimensions —
        // they arrive once the bridge is connected (poll in effect above).
        // Taps/drags rely on them for coordinate mapping.
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 text-center max-w-sm mx-auto">
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full bg-accent-cyan/20 blur-xl animate-pulse"
            />
            <div className="relative w-16 h-16 rounded-2xl bg-surface-800 border border-surface-700 flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-accent-cyan" />
            </div>
          </div>
          <div>
            <div className="text-sm text-surface-200 font-medium">
              {device?.name
                ? `Waiting for ${device.name}`
                : "Waiting for device…"}
            </div>
            <div className="mt-1 text-xs text-surface-500">
              {device?.bridgeRunning === false
                ? "Starting bridge on the device — this can take a moment (iOS simulators warm up, signing runs on first start)."
                : "Connecting to the bridge…"}
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs text-surface-500">
            <Loader2 className="w-3.5 h-3.5 text-accent-cyan animate-spin" />
            <span>Stream will start automatically</span>
          </div>
        </div>
      )}
    </div>
  );
}
